import { useEffect, useState, type FormEvent } from "react";
type Alias = { address: string; recipients: string[]; active: boolean };
type Resource = {
  address: string;
  name: string;
  kind: "location" | "thing";
  active: boolean;
  bookingLimit: number;
};
type Organization = { aliases: Alias[]; resources: Resource[] };
export function StaffOrganization({
  api,
  mutate,
  mailboxes,
}: {
  api: <T>(path: string) => Promise<T>;
  mutate: <T>(path: string, body: unknown) => Promise<T>;
  mailboxes: { address: string; name: string; active: boolean }[];
}) {
  const [data, setData] = useState<Organization | null>(null),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [revision, setRevision] = useState(0),
    [mode, setMode] = useState<"aliases" | "resources" | null>(null),
    [selected, setSelected] = useState<Alias | Resource | null>(null);
  useEffect(() => {
    let current = true;
    api<Organization>("/admin/staff-email/organization")
      .then((value) => {
        if (current) {
          setData(value);
          setError("");
        }
      })
      .catch((cause) => {
        if (current) setError((cause as Error).message);
      });
    return () => {
      current = false;
    };
  }, [api, revision]);
  const finish = () => {
    setMode(null);
    setSelected(null);
    setRevision((n) => n + 1);
  };
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const body =
        mode === "aliases"
          ? {
              ...(selected ? {} : { localPart: form.get("localPart") }),
              recipients: form.getAll("recipients"),
              active: form.get("active") === "on",
            }
          : {
              name: form.get("name"),
              kind: form.get("kind"),
              active: form.get("active") === "on",
            };
      await mutate(
        "/admin/staff-email/organization/" +
          mode +
          (selected ? "/" + encodeURIComponent(selected.address) : ""),
        body,
      );
      setNotice(
        mode === "aliases" ? "Team address saved." : "Bookable resource saved.",
      );
      finish();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function remove(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      await mutate(
        "/admin/staff-email/organization/" +
          mode +
          "/" +
          encodeURIComponent(selected.address) +
          "/delete",
        {
          confirmAddress: new FormData(event.currentTarget).get(
            "confirmAddress",
          ),
        },
      );
      setNotice("Organization item deleted.");
      finish();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function edit(next: "aliases" | "resources", item: Alias | Resource | null) {
    setMode(next);
    setSelected(item);
    setNotice("");
    setError("");
  }
  return (
    <>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <section className="cr-panel">
        <div className="cr-dashboard-section-heading">
          <h2>Team addresses</h2>
          <button
            className="cr-button secondary"
            disabled={busy || !data || !mailboxes.some((m) => m.active)}
            onClick={() => edit("aliases", null)}
          >
            Add team address
          </button>
        </div>
        <p>
          Deliver mail sent to one address to the selected staff members. Each
          person receives a copy in their own inbox.
        </p>
        {!mailboxes.length && (
          <p className="cr-form-note">
            Create staff mailboxes above before adding distribution addresses.
          </p>
        )}
        {data?.aliases.length ? (
          <ul className="cr-service-status-list">
            {data.aliases.map((item) => (
              <li key={item.address}>
                <div>
                  <strong>{item.address}</strong>
                  <p>
                    {item.active ? "Active" : "Disabled"} ·{" "}
                    {item.recipients.join(", ")}
                  </p>
                </div>
                <button
                  className="cr-text-button"
                  disabled={busy}
                  onClick={() => edit("aliases", item)}
                >
                  Manage
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>No team addresses yet.</p>
        )}
      </section>
      <section className="cr-panel">
        <div className="cr-dashboard-section-heading">
          <h2>Rooms & equipment</h2>
          <button
            className="cr-button secondary"
            disabled={busy || !data}
            onClick={() => edit("resources", null)}
          >
            Add resource
          </button>
        </div>
        <p>
          Invite a resource’s email address to a calendar event to reserve it.
          Each resource permits one booking at a time.
        </p>
        {data?.resources.length ? (
          <ul className="cr-service-status-list">
            {data.resources.map((item) => (
              <li key={item.address}>
                <div>
                  <strong>{item.name}</strong>
                  <p>
                    {item.kind === "location" ? "Room" : "Equipment"} ·{" "}
                    {item.active ? "Active" : "Disabled"}
                  </p>
                  <p>{item.address}</p>
                </div>
                <button
                  className="cr-text-button"
                  disabled={busy}
                  onClick={() => edit("resources", item)}
                >
                  Manage
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>No bookable resources yet.</p>
        )}
      </section>
      {mode && (
        <section className="cr-panel">
          <h2>
            {selected
              ? "Manage " + selected.address
              : mode === "aliases"
                ? "Add team address"
                : "Add room or equipment"}
          </h2>
          <form
            className="cr-form"
            key={mode + ":" + (selected?.address || "new")}
            onSubmit={save}
          >
            {mode === "aliases" ? (
              <>
                {!selected && (
                  <label>
                    Address
                    <input
                      name="localPart"
                      required
                      maxLength={64}
                      autoCapitalize="none"
                      autoComplete="off"
                    />
                    @cacherivertelephone.com
                  </label>
                )}
                <fieldset>
                  <legend>Staff recipients</legend>
                  {mailboxes
                    .filter((m) => m.active)
                    .map((m) => (
                      <label key={m.address}>
                        <input
                          type="checkbox"
                          name="recipients"
                          value={m.address}
                          defaultChecked={
                            !!selected &&
                            "recipients" in selected &&
                            selected.recipients.includes(m.address)
                          }
                        />
                        {m.name} · {m.address}
                      </label>
                    ))}
                </fieldset>
              </>
            ) : (
              <>
                <label>
                  Resource name
                  <input
                    name="name"
                    required
                    maxLength={100}
                    defaultValue={
                      selected && "name" in selected ? selected.name : ""
                    }
                  />
                </label>
                <label>
                  Type
                  <select
                    name="kind"
                    defaultValue={
                      selected && "kind" in selected
                        ? selected.kind
                        : "location"
                    }
                  >
                    <option value="location">Room</option>
                    <option value="thing">Equipment</option>
                  </select>
                </label>
                <p className="cr-form-note">
                  The booking address is generated from the name. Existing
                  addresses stay the same when a resource is renamed.
                </p>
              </>
            )}
            <label>
              <input
                name="active"
                type="checkbox"
                defaultChecked={selected?.active ?? true}
              />
              Enabled
            </label>
            <button className="cr-button" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="cr-button secondary"
              disabled={busy}
              onClick={() => {
                setMode(null);
                setSelected(null);
              }}
            >
              Cancel
            </button>
          </form>
          {selected && (
            <form className="cr-form" onSubmit={remove}>
              <h3>Delete {mode === "aliases" ? "team address" : "resource"}</h3>
              <p>
                {mode === "aliases"
                  ? "Mail will no longer be distributed through this address."
                  : "This removes the resource and its booking calendar."}
              </p>
              <label>
                Type {selected.address} to confirm
                <input
                  name="confirmAddress"
                  type="email"
                  required
                  autoComplete="off"
                />
              </label>
              <button className="cr-button secondary" disabled={busy}>
                Delete permanently
              </button>
            </form>
          )}
        </section>
      )}
      <section className="cr-panel">
        <h2>Calendars, contacts & shared access</h2>
        <p>
          Staff calendars and contacts are available in webmail. Calendar
          sharing and delegated inbox access are managed by the owner in the
          calendar or mail folder’s sharing settings.
        </p>
        <p>
          Share with named staff accounts and choose the permissions they need.
          A distribution address sends copies of messages; it does not create a
          shared inbox.
        </p>
        <a
          className="cr-button secondary"
          href="https://mail.cacherivertelephone.com/SOGo/so/"
          target="_blank"
          rel="noreferrer"
        >
          Open staff webmail
        </a>
      </section>
    </>
  );
}
